/** Depth screens over the map (docs/ui-decisions.md, "Depth screens"):
 *  Research, Designs and Market. Opened from the top bar or a
 *  key, closed with Esc or the same key; the world runs on underneath. One
 *  is open at a time. They read the commander's state (the server's
 *  `state` message) and send commands back. */

import { series } from "../state/history";
import { For, Show, createEffect, createSignal, type JSX } from "solid-js";
import { ORE_HUES, oreKey, oreLabel, oreRGB, oreWord } from "../render/colour";
import { render } from "solid-js/web";
import { DesignScreen } from "./Designs";
import { ProductionScreen } from "./Production";
import { DiplomacyScreen } from "./Diplomacy";
import { AdminPanel } from "./Admin";
import { sideName } from "../state/players";
import { GuideScreen, closeGuide } from "./Guides";
import { GoodIcon } from "../console/RecipePicker";

export type ScreenId = "research" | "designs" | "market" | "production" | "diplomacy" | "admin";

const [screen, setScreen] = createSignal<ScreenId | null>(null);
const [state, setState] = createSignal<Record<string, unknown> | null>(null);
let send: (command: Record<string, unknown>) => void = () => {};
let factions: { name: string; color: string }[] = [];
let bake: (spec: object) => Promise<Record<string, unknown>> = async () => ({});
let toast: (text: string) => void = () => {};
/** Designs that can be traded as units, with how many of yours there are
 *  (the game sets it). */
let designsForTrade: () => { id: number; name: string; have: number }[] = () => [];

/** A technology just asked for: shown as researched at once, until the
 *  server's state says so (or, a few seconds on, says otherwise). */
const [asked, setAsked] = createSignal<{ tech: number; at: number } | null>(null);

export { screen, setState as setScreenState };

/** Picks a box on the map (two corners), with the screens out of the way
 *  meanwhile (the game sets it). */
let boxPicker: (cb: (box: [[number, number], [number, number]]) => void) => void = () => {};
export function setBoxPicker(f: typeof boxPicker): void {
  boxPicker = f;
}

/** Opens `id`, or closes it if it's open. */
export function toggleScreen(id: ScreenId): void {
  setScreen((s) => (s === id ? null : id));
}

export function closeScreen(): boolean {
  // A first-time guide is on top of any screen: it closes first.
  if (closeGuide()) return true;
  const open = screen() !== null;
  setScreen(null);
  return open;
}

// The state's shapes (server: ferro-server/src/state.rs).
type Metric = { Output: { good: number } };
type Requirement =
  | { Consume: { good: number; amount: number } }
  | { Prerequisite: { tech: number } }
  | { Milestone: { metric: Metric; at_least: number } }
  | { Sustained: { metric: Metric; at_least: number; ticks: number } };
type Capability = { Material: string } | { Engineering: string } | { Boost: string };
interface Tech {
  id: number;
  name: string;
  branch: string;
  requirements: Requirement[];
  unlocks: Capability[];
  /** Each unlock in plain words ("Mines extract 50% faster"). */
  effects?: string[];
}
interface Progress {
  requirement: Requirement;
  current: number;
  needed: number;
  met: boolean;
}
interface ResearchState {
  /** The last pick the server turned down, and why (player-ready). */
  refused?: { tech: number; why: string; tick: number } | null;
  /** Goods given so far, per technology with any (by requirement). */
  given?: Record<string, number[]>;
  techs: Tech[];
  known: number[];
  pursuing: number | null;
  progress: Progress[] | null;
}

const TICKS_PER_MINUTE = 600;

/** Each requirement's progress when it last moved (by "tech:index"): the
 *  server works research in 30 s steps, so between them a held rate is
 *  counted on at the game's pace from there. */
/** Each requirement's last report and how fast it moved before it (by
 *  "tech:index"), for "about 12 min at 530/min". */
const pace = new Map<string, { current: number; at: number; perMin: number }>();

/** Minutes in words: "under a minute", "about 12 min", "about 3 h 20 min". */
function aboutMinutes(m: number): string {
  if (m < 1) return "under a minute";
  if (m < 60) return `about ${Math.round(m)} min`;
  const h = Math.floor(m / 60);
  const rest = Math.round(m % 60);
  return `about ${h} h${rest ? ` ${rest} min` : ""}`;
}

/** A trailing window in words: "the past hour", "the past 30 min". */
function pastWindow(ticks: number): string {
  const minutes = Math.max(1, Math.round(ticks / TICKS_PER_MINUTE));
  return minutes === 60 ? "the past hour" : minutes % 60 === 0 ? `the past ${minutes / 60} hours` : `the past ${minutes} min`;
}

/** The four trees, in the order they're stacked, and each one's ore (its
 *  atlas hue). */
const TREES = ["Industry", "Logistics", "Engineering", "Military"];
const TREE_ORE: Record<string, string> = { Industry: "Iron", Logistics: "Copper", Engineering: "Coal", Military: "Oil" };

function ResearchScreen(): JSX.Element {
  const r = () => (state()?.research as ResearchState | undefined) ?? null;
  const goods = () => (state()?.goods as string[] | undefined) ?? [];
  const good = (g: number) => goods()[g] ?? `Good ${g}`;
  const tech = (id: number) => r()?.techs.find((t) => t.id === id);
  const techName = (id: number) => tech(id)?.name ?? `Technology ${id}`;
  const known = (id: number) => r()?.known.includes(id) ?? false;
  const metric = (m: Metric) => `${good(m.Output.good)} a minute`;
  // What a requirement asks, in a few words.
  const ask = (q: Requirement, branch: string): string => {
    if ("Consume" in q) return `${q.Consume.amount.toLocaleString("en-US")} ${good(q.Consume.good).toLowerCase()}`;
    if ("Prerequisite" in q) {
      const other = tech(q.Prerequisite.tech);
      return `After ${techName(q.Prerequisite.tech)}${other && other.branch !== branch ? `, ${other.branch}` : ""}`;
    }
    if ("Milestone" in q) return `${q.Milestone.at_least.toLocaleString("en-US")} ${metric(q.Milestone.metric).toLowerCase()}`;
    // An average over the past window (output already made counts).
    return `${q.Sustained.at_least.toLocaleString("en-US")} ${good(q.Sustained.metric.Output.good).toLowerCase()}/min averaged over ${pastWindow(q.Sustained.ticks)}`;
  };
  // A technology's state: known, pursued, available (prerequisites known)
  // or locked.
  const status = (t: Tech) =>
    known(t.id)
      ? "known"
      : pursuing() === t.id
        ? "pursued"
        : t.requirements.every((q) => !("Prerequisite" in q) || known(q.Prerequisite.tech))
          ? "available"
          : "locked";
  const prereqs = (t: Tech) => t.requirements.flatMap((q) => ("Prerequisite" in q ? [q.Prerequisite.tech] : []));
  // What's being researched: the one just asked for, until the server's
  // state confirms it (or turns it down: then back, and why).
  const pursuing = () => asked()?.tech ?? r()?.pursuing ?? null;
  createEffect(() => {
    const a = asked();
    const now = r();
    if (!a || !now) return;
    if (now.pursuing === a.tech || now.known.includes(a.tech)) {
      setAsked(null);
    } else if (now.refused?.tech === a.tech) {
      // Turned down, with the server's reason (shown by the effect below).
      setAsked(null);
    } else if (performance.now() - a.at > 3000) {
      setAsked(null);
      const t = tech(a.tech);
      const missing = t ? prereqs(t).filter((p) => !known(p)) : [];
      toast(`${techName(a.tech)} not started: ${missing.length ? `needs ${missing.map(techName).join(" and ")} first` : "the server turned it down"}`);
    }
  });
  // A pick the server turned down: its reason, once.
  let refusedAt = -1;
  createEffect(() => {
    const f = r()?.refused;
    if (!f || f.tick === refusedAt) return;
    const first = refusedAt === -1;
    refusedAt = f.tick;
    // Not on opening the screen (an old refusal), only as it comes.
    if (!first || asked()) toast(`${techName(f.tech)} not started: ${f.why}`);
  });
  // What a technology still lacks before it can be started: research only
  // starts with every good to give already held (summed over your
  // stations) and every rate already met, so a pick never strands goods.
  const stockOf = (g: number) =>
    ((state()?.economy as { goods?: { good: number; stock: number; made_per_min: number }[] } | undefined)?.goods ?? []).find((x) => x.good === g);
  const shortOf = (t: Tech): string[] => {
    const out: string[] = [];
    const mine = r()?.pursuing === t.id ? (r()?.progress ?? []) : [];
    // Goods already given to it (each technology keeps its own).
    const banked = r()?.given?.[String(t.id)] ?? [];
    t.requirements.forEach((q, k) => {
      const given = mine[k]?.current ?? banked[k] ?? 0;
      if ("Consume" in q) {
        const need = q.Consume.amount - given;
        const have = stockOf(q.Consume.good)?.stock ?? 0;
        if (need > have) out.push(`${Math.ceil(need - have).toLocaleString("en-US")} more ${good(q.Consume.good).toLowerCase()}`);
      } else if ("Milestone" in q) {
        const now = stockOf(q.Milestone.metric.Output.good)?.made_per_min ?? 0;
        if (now < q.Milestone.at_least) out.push(`${q.Milestone.at_least.toLocaleString("en-US")} ${good(q.Milestone.metric.Output.good).toLowerCase()}/min (now ${Math.round(now).toLocaleString("en-US")})`);
      } else if ("Sustained" in q) {
        const g = q.Sustained.metric.Output.good;
        // The trailing average: the pursued one's from the server, else the
        // production history's.
        let avg = mine[k]?.current;
        if (avg === undefined) {
          const pts = series(good(g)) ?? [];
          const end = pts[pts.length - 1]?.tick ?? 0;
          const win = pts.filter((x) => x.tick > end - q.Sustained.ticks);
          avg = win.length ? win.reduce((a, x) => a + x.made, 0) / win.length : 0;
        }
        if (avg < q.Sustained.at_least)
          out.push(`${q.Sustained.at_least.toLocaleString("en-US")} ${good(g).toLowerCase()}/min averaged over ${pastWindow(q.Sustained.ticks)} (now ${Math.round(avg).toLocaleString("en-US")})`);
      }
    });
    return out;
  };
  const research = (t: Tech) => {
    if (shortOf(t).length) return;
    setAsked({ tech: t.id, at: performance.now() });
    send({ type: "research", tech: t.id });
    toast(`Researching ${t.name}`);
  };
  // Depth: the longest chain of prerequisites under it (roots are 0).
  const depth = (id: number, seen = new Set<number>()): number => {
    const t = tech(id);
    if (!t || seen.has(id)) return 0;
    seen.add(id);
    return Math.max(0, ...prereqs(t).map((p) => depth(p, seen) + 1));
  };
  // Each tree laid out: a column a depth, each column ordered to sit near
  // what it builds on (its prerequisites' rows, on average).
  const layout = (branch: string) => {
    const techs = (r()?.techs ?? []).filter((t) => t.branch === branch);
    const cols = new Map<number, Tech[]>();
    for (const t of techs) {
      const d = depth(t.id);
      cols.set(d, [...(cols.get(d) ?? []), t]);
    }
    const row = new Map<number, number>();
    const placed: { t: Tech; col: number; row: number }[] = [];
    for (const d of [...cols.keys()].sort((a, b) => a - b)) {
      const here = cols.get(d)!;
      const weight = (t: Tech) => {
        const rows = prereqs(t).flatMap((p) => (row.has(p) ? [row.get(p)!] : []));
        return rows.length ? rows.reduce((a, b) => a + b, 0) / rows.length : t.id;
      };
      here.sort((a, b) => weight(a) - weight(b));
      here.forEach((t, k) => {
        row.set(t.id, k);
        placed.push({ t, col: d, row: k });
      });
    }
    return placed;
  };
  // How fast each requirement is moving: measured between reports where it
  // changed (a minute's worth of reports smooths it). The version tells
  // the lines reading it that it moved.
  const [paced, setPaced] = createSignal(0);
  createEffect(() => {
    const now = r();
    if (now?.pursuing === null || now?.pursuing === undefined) return;
    const t = performance.now();
    (now.progress ?? []).forEach((p, k) => {
      const key = `${now.pursuing}:${k}`;
      const was = pace.get(key);
      if (!was) pace.set(key, { current: p.current, at: t, perMin: 0 });
      else if (p.current !== was.current) {
        const rate = ((p.current - was.current) / Math.max(1, t - was.at)) * 60000;
        pace.set(key, { current: p.current, at: t, perMin: was.perMin ? was.perMin * 0.5 + rate * 0.5 : rate });
      } else if (t - was.at > 120000) pace.set(key, { ...was, perMin: 0 });
    });
    setPaced((v) => v + 1);
  });
  // What a requirement still needs, and how soon at this pace.
  const eta = (p: Progress, k: number): string => {
    paced();
    const rate = pace.get(`${r()?.pursuing}:${k}`)?.perMin ?? 0;
    const left = p.needed - p.current;
    if (left <= 0) return "";
    return rate > 0 ? ` · ${aboutMinutes(left / rate)} at ${Math.round(rate).toLocaleString("en-US")}/min` : "";
  };
  const share = (p: Progress, _k = -1) => (p.met ? 1 : p.needed > 0 ? Math.min(1, p.current / p.needed) : 0);
  const unlock = (c: Capability) => Object.values(c)[0] as string;
  // The pursued technology's progress over what it asks (prerequisites
  // aside), 0..1.
  const progressOf = (t: Tech) => {
    if (status(t) !== "pursued" || r()?.pursuing !== t.id) return 0;
    const ps = (r()?.progress ?? []).map((p, k) => [p, k] as const).filter(([p]) => !("Prerequisite" in p.requirement));
    return ps.length ? ps.reduce((a, [p, k]) => a + share(p, k), 0) / ps.length : 0;
  };
  // The technology shown in the panel: the one hovered, else the one
  // picked, else the one being researched.
  const [hovered, setHovered] = createSignal<number | null>(null);
  const [picked, setPicked] = createSignal<number | null>(null);
  const shown = () => tech(hovered() ?? picked() ?? pursuing() ?? -1);
  // The lattice: flat-topped hexes in columns, a column a depth (so depth
  // alone runs left to right), odd columns half a hex down, so a
  // technology beside what it needs shares an edge with it.
  const R = 52;
  const H = Math.sqrt(3) * R;
  const GAP = 8;
  const at = (col: number, row: number): [number, number] => [R + 4 + col * (1.5 * R + GAP), H / 2 + 4 + row * (H + GAP) + (col % 2 ? (H + GAP) / 2 : 0)];
  const hexPoints = (x: number, y: number, rr = R) =>
    [0, 60, 120, 180, 240, 300].map((a) => `${(x + rr * Math.cos((a * Math.PI) / 180)).toFixed(1)},${(y + rr * Math.sin((a * Math.PI) / 180)).toFixed(1)}`).join(" ");
  // A name in up to three lines of about twelve letters.
  const lines = (name: string) => {
    const out: string[] = [];
    for (const w of name.split(" ")) {
      const last = out[out.length - 1];
      if (last !== undefined && (last + " " + w).length <= 12) out[out.length - 1] = `${last} ${w}`;
      else out.push(w);
    }
    return out.slice(0, 3);
  };
  // The heat: a tree's atlas hue, burning by state (flat fills).
  const DARK: [number, number, number] = [0.09, 0.09, 0.085];
  const css = (c: number[]) => `rgb(${c.map((v) => Math.round(v * 255)).join(",")})`;
  const mix = (a: number[], b: number[], t: number) => a.map((v, i) => v + (b[i] - v) * t);
  const heat = (branch: string, st: string) => {
    const hue = oreRGB(TREE_ORE[branch] ?? "Coal");
    return st === "known"
      ? { fill: css(mix(hue, [1, 0.96, 0.88], 0.15)), stroke: css(mix(hue, [1, 1, 1], 0.35)), text: "#141413" }
      : st === "pursued"
        ? { fill: css(mix(DARK, hue, 0.28)), stroke: css(hue), text: "#f2efe8", level: css(mix(hue, DARK, 0.15)) }
        : st === "available"
          ? { fill: css(mix(DARK, hue, 0.2)), stroke: css(hue), text: "#f2efe8" }
          : { fill: css(mix(DARK, hue, 0.07)), stroke: css(mix(DARK, hue, 0.3)), text: "#7d7a72" };
  };
  return (
    <section class="depth-screen research" aria-label="Research">
      <Show when={r()} fallback={<p class="muted">Waiting for your research…</p>}>
        <div class="research-layout">
          <div class="trees">
            {/* The four trees in their order, then any other branch there is. */}
            <For each={[...TREES, ...new Set(r()!.techs.map((t) => t.branch))].filter((b, i, all) => all.indexOf(b) === i && r()!.techs.some((t) => t.branch === b))}>
              {(branch) => {
                const placed = () => layout(branch);
                const size = () => {
                  const cols = Math.max(1, ...placed().map((p) => p.col + 1));
                  const rows = Math.max(1, ...placed().map((p) => p.row + 1));
                  return [Math.ceil(cols * (1.5 * R + GAP) + R / 2 + 12), Math.ceil((rows + 0.5) * (H + GAP) + 12)];
                };
                const pos = (id: number) => {
                  const p = placed().find((x) => x.t.id === id);
                  return p ? at(p.col, p.row) : null;
                };
                return (
                  <div class="tree">
                    <h2 style={{ color: css(oreRGB(TREE_ORE[branch] ?? "Coal")) }}>{branch}</h2>
                    <svg class="hex-tree" width={size()[0]} height={size()[1]}>
                      {/* Links under the hexes: each to what it needs. */}
                      <For each={placed().flatMap(({ t }) => prereqs(t).flatMap((p) => (pos(p) ? [[p, t.id] as [number, number]] : [])))}>
                        {([a, b]) => {
                          const [x0, y0] = pos(a)!;
                          const [x1, y1] = pos(b)!;
                          return <line class="hex-link" classList={{ done: known(a) }} x1={x0} y1={y0} x2={x1} y2={y1} style={{ stroke: known(a) ? css(oreRGB(TREE_ORE[branch] ?? "Coal")) : undefined }} />;
                        }}
                      </For>
                      <For each={placed()}>
                        {({ t, col, row }) => {
                          const [x, y] = at(col, row);
                          const h = () => heat(branch, status(t));
                          const level = () => progressOf(t);
                          return (
                            <g
                              class="hex-tech"
                              classList={{ picked: shown()?.id === t.id }}
                              onPointerEnter={() => setHovered(t.id)}
                              onPointerLeave={() => setHovered(null)}
                              onClick={() => setPicked(t.id)}
                            >
                              <polygon points={hexPoints(x, y)} fill={h().fill} stroke={h().stroke} stroke-width={status(t) === "locked" ? 1 : 2} />
                              {/* Researching: the hue rising from the bottom with progress. */}
                              <Show when={status(t) === "pursued"}>
                                <clipPath id={`lvl-${t.id}`}>
                                  <rect x={x - R} y={y + H / 2 - H * level()} width={2 * R} height={H * level()} />
                                </clipPath>
                                <polygon points={hexPoints(x, y, R - 1)} fill={h().level} clip-path={`url(#lvl-${t.id})`} />
                              </Show>
                              <For each={lines(t.name)}>
                                {(l, k) => (
                                  <text class="hex-name" x={x} y={y + (k() - (lines(t.name).length - 1) / 2) * 14 + 4} fill={h().text}>
                                    {l}
                                  </text>
                                )}
                              </For>
                              <Show when={status(t) === "known"}>
                                <text class="hex-mark" x={x} y={y + H / 2 - 8} fill={h().text}>
                                  ✓
                                </text>
                              </Show>
                            </g>
                          );
                        }}
                      </For>
                    </svg>
                  </div>
                );
              }}
            </For>
          </div>
          {/* The one hovered, picked or being researched: what it does and
              asks, and Research. */}
          <aside class="tech-panel">
            <Show when={shown()} fallback={<p class="muted">Point at a technology to see what it does.</p>}>
              {(t) => (
                <>
                  <h3 style={{ color: css(oreRGB(TREE_ORE[t().branch] ?? "Coal")) }}>{t().name}</h3>
                  <p class="muted small">
                    {t().branch} · {{ known: "Done", pursued: "Researching", available: "Can be researched", locked: "Locked" }[status(t())]}
                  </p>
                  <Show when={(t().effects?.length ?? 0) > 0 || t().unlocks.length}>
                    <ul class="tech-effects">
                      <For each={t().effects?.length ? t().effects : t().unlocks.map(unlock)}>{(e) => <li>{e}</li>}</For>
                    </ul>
                  </Show>
                  <div class="tech">
                    <For each={t().requirements}>
                      {(q, k) => {
                        const p = () => (r()?.pursuing === t().id ? r()?.progress?.[k()] : undefined);
                        const met = () => known(t().id) || p()?.met || ("Prerequisite" in q && known(q.Prerequisite.tech));
                        // Being researched and not met yet: the one(s) still open.
                        const open = () => !!p() && !met();
                        return (
                          <span class="req" classList={{ met: !!met(), open: open() }}>
                            <span>
                              {met() ? "✓ " : ""}
                              {ask(q, t().branch)}
                              <Show when={open()}>
                                <span class="req-open"> · still needed</span>
                              </Show>
                            </span>
                            <Show when={p() && !("Prerequisite" in q)}>
                              <span class="bar">
                                <span style={{ width: `${share(p()!, k()) * 100}%` }} />
                              </span>
                            </Show>
                            {/* Goods to give or a total to reach: how much so far, and
                                how soon at the pace it's going. */}
                            <Show when={p() && ("Consume" in q || "Milestone" in q) && !met() ? p() : null}>
                              {(pp) => (
                                <span class="req-detail">
                                  {Math.round(pp().current).toLocaleString("en-US")} / {pp().needed.toLocaleString("en-US")}
                                  {"Consume" in q ? ` ${good(q.Consume.good).toLowerCase()}` : " a min"}
                                  {eta(pp(), k())}
                                </span>
                              )}
                            </Show>
                            {/* An average over the past window: where it stands now
                                against what's asked. */}
                            <Show when={p() && "Sustained" in q ? (q as { Sustained: { metric: Metric; at_least: number; ticks: number } }).Sustained : null}>
                              {(sq) => (
                                <span class="req-detail">
                                  {good(sq().metric.Output.good)} output, {pastWindow(sq().ticks).replace("the ", "")}: {Math.round(p()!.current).toLocaleString("en-US")} / {sq().at_least.toLocaleString("en-US")} a min
                                  {p()!.current >= sq().at_least ? " ✓" : ""}
                                </span>
                              )}
                            </Show>
                          </span>
                        );
                      }}
                    </For>
                  </div>
                  <Show when={status(t()) === "available"}>
                    {/* Only once it can be paid for in full: what's short, said. */}
                    <Show when={shortOf(t()).length}>
                      <p class="tech-short">Can't start yet: needs {shortOf(t()).join(", ")}.</p>
                    </Show>
                    <button
                      class="primary tech-go"
                      aria-disabled={shortOf(t()).length ? "true" : undefined}
                      data-tip={shortOf(t()).length ? `Can't start yet: needs ${shortOf(t()).join(", ")}` : undefined}
                      onClick={() => research(t())}
                    >
                      Research
                    </button>
                  </Show>
                  <Show when={status(t()) === "pursued"}>
                    <button class="primary tech-go" disabled>
                      Researching…
                    </button>
                  </Show>
                </>
              )}
            </Show>
          </aside>
        </div>
      </Show>
    </section>
  );
}

/** One side of a deal: goods [good, n], credits, a box of prospecting
 *  findings or of last-seen sightings (copied over at once), units, or a
 *  report (a claim, true or not, of so much of an ore in each cell of a
 *  box: [corner, corner, ore, per cell]). */
type Lot =
  | [number, number]
  | { credits: number }
  | { findings: [[number, number], [number, number]] }
  | { sightings: [[number, number], [number, number]] }
  | { report: [[number, number], [number, number], string, number] }
  | { units: [number, number] }
  | { feed: { delay: number; hours: number; own_ground: boolean } };

interface Offer {
  id: number;
  seller: number;
  at: number;
  give: Lot;
  take: Lot;
  /** Reference worth of each side (at-cost credits), not a price. */
  worth?: [number, number];
  /** A standing deal: renews round after round once taken. */
  repeat?: boolean;
}
interface Contract {
  id: number;
  seller: number;
  buyer: number;
  give: Lot;
  take: Lot;
  worth?: [number, number];
  sent: number;
  paid: number;
  /** A standing deal, and how many rounds it's run. */
  repeat?: boolean;
  rounds?: number;
  /** Units of it walking over (their keys). */
  moving?: string[];
}
interface Station {
  id: number;
  at: [number, number];
  /** Per freight good (id 1 up). */
  freight: number[];
  /** Rounds in its store, where known. */
  stock?: number;
}

/** Goods that trade: rounds (from a station's store beyond half) and
 *  freight; not recruits. */
const TRADED = [0, 1, 2, 3, 4, 5, 6, 7];

// The offer being drawn up, kept while the market's closed (picking a box
// of findings on the map closes it): each side a kind of lot and its amount.
type Kind = "goods" | "credits" | "findings" | "sightings" | "report" | "units" | "feed";
// A feed's terms, each side's (what you give, or ask of them).
const [giveFeed, setGiveFeed] = createSignal({ delay: 5, hours: 2, own_ground: false });
const [takeFeed, setTakeFeed] = createSignal({ delay: 5, hours: 2, own_ground: false });
const REPORT_ORES = ORE_HUES.map(([o]) => oreWord(o));
const [reportOre, setReportOre] = createSignal("coal");
const [reportAmount, setReportAmount] = createSignal(2000);
const [giveKind, setGiveKind] = createSignal<Kind>("goods");
const [takeKind, setTakeKind] = createSignal<Kind>("goods");
const [give, setGive] = createSignal(5);
const [giveN, setGiveN] = createSignal(500);
const [take, setTake] = createSignal(6);
const [takeN, setTakeN] = createSignal(300);
const [giveBox, setGiveBox] = createSignal<[[number, number], [number, number]] | null>(null);
const [giveDesign, setGiveDesign] = createSignal(0);
const [takeDesign, setTakeDesign] = createSignal(0);
const [standing, setStanding] = createSignal(false);

/** The AI market (trade posts): this hour's offers to buy, delivered to a
 *  post of yours, paid in credits. */
function AiMarket(): JSX.Element {
  const m = () => (state()?.ai_market as { offers?: { good: number; name: string; left: number; price: number }[]; posts?: { at: [number, number]; sells: number[] }[] } | undefined) ?? {};
  const credits = () => (state()?.credits as number | undefined) ?? 0;
  const [amounts, setAmounts] = createSignal<Record<number, number>>({});
  const [postFor, setPostFor] = createSignal<string>("");
  const posts = () => m().posts ?? [];
  const post = () => posts().find((p) => p.at.join(",") === postFor()) ?? posts()[0];
  return (
    <div class="ai-market">
      <h2>AI market</h2>
      <Show
        when={posts().length}
        fallback={<p class="muted">Build a trade post on your track (Build tab) to sell to the AI market and buy from it.</p>}
      >
        <p class="muted small">
          {credits().toLocaleString("en-US", { maximumFractionDigits: 0 })} credits. Each hour three goods on offer at a premium; what you buy arrives at the post you pick. Choose what a post sells on its card.
        </p>
        <Show when={posts().length > 1}>
          <label class="plan-option">
            Deliver to
            <select value={post()?.at.join(",")} onChange={(e) => setPostFor(e.currentTarget.value)}>
              <For each={posts()}>{(p) => <option value={p.at.join(",")}>Trade post at {p.at.join(", ")}</option>}</For>
            </select>
          </label>
        </Show>
        <table>
          <thead>
            <tr>
              <th>Good</th>
              <th>Left</th>
              <th>Each</th>
              <th />
            </tr>
          </thead>
          <tbody>
            <For each={m().offers ?? []} fallback={<tr><td colspan="4" class="muted">Nothing on offer this hour.</td></tr>}>
              {(o) => {
                const n = () => Math.min(o.left, amounts()[o.good] ?? Math.min(o.left, 100));
                return (
                  <tr>
                    <td>
                      <span class="ai-good">
                        <GoodIcon good={o.name} size={20} />
                        {o.name}
                      </span>
                    </td>
                    <td>{o.left.toLocaleString("en-US")}</td>
                    <td>{o.price.toLocaleString("en-US", { maximumFractionDigits: 1 })} cr</td>
                    <td class="ai-buy">
                      <input type="number" min="1" max={o.left} value={n()} onInput={(e) => setAmounts({ ...amounts(), [o.good]: Math.max(1, Math.floor(Number(e.currentTarget.value) || 1)) })} />
                      <button
                        class="button"
                        aria-disabled={!o.left || !post() || credits() < o.price ? "true" : undefined}
                        data-tip={credits() < o.price ? "Not enough credits" : `${n()} for about ${Math.round(n() * o.price).toLocaleString("en-US")} credits, as far as your credits go`}
                        onClick={() => o.left && post() && credits() >= o.price && send({ type: "market_buy", at: post()!.at, good: o.good, amount: n() })}
                      >
                        Buy
                      </button>
                    </td>
                  </tr>
                );
              }}
            </For>
          </tbody>
        </table>
      </Show>
    </div>
  );
}

function MarketScreen(): JSX.Element {
  const me = () => (state()?.player as number | undefined) ?? -1;
  const goods = () => (state()?.goods as string[] | undefined) ?? [];
  const good = (g: number) => (goods()[g] ?? `Good ${g}`).toLowerCase();
  const market = () => (state()?.market as { offers: Offer[]; contracts: Contract[] } | undefined) ?? { offers: [], contracts: [] };
  const stations = () => (state()?.stations as Station[] | undefined) ?? [];
  const held = (st: Station, g: number) => (g === 0 ? (st.stock ?? 0) : (st.freight[g - 1] ?? 0));
  // Rounds: the economy's count when the stations don't say.
  const total = (g: number) => {
    const sum = stations().reduce((n, st) => n + held(st, g), 0);
    if (g !== 0 || sum > 0) return sum;
    return ((state()?.economy as { goods?: { good: number; stock: number }[] } | undefined)?.goods ?? []).find((x) => x.good === 0)?.stock ?? 0;
  };
  // The station holding the most of a good: where it ships from (and,
  // buying, where payment leaves and the goods arrive).
  const richest = (g: number) => stations().reduce<Station | null>((best, st) => (!best || held(st, g) > held(best, g) ? st : best), null);
  const amount = (n: number, g: number) => `${n.toLocaleString("en-US")} ${good(g)}`;
  const who = (f: number) => (
    <span class="who">
      <span class="dot" style={{ background: factions[f]?.color ?? "#888" }} />
      {f === me() ? "You" : sideName(f)}
    </span>
  );
  const credits = () => (state()?.credits as number | undefined) ?? 0;
  const prices = () => (state()?.prices as number[] | undefined) ?? [];
  const lotOf = (kind: Kind, g: number, n: number, box: [[number, number], [number, number]] | null, design: number): Lot | null =>
    kind === "goods"
      ? [g, n]
      : kind === "credits"
        ? { credits: n }
        : kind === "findings"
          ? box && { findings: box }
          : kind === "sightings"
            ? box && { sightings: box }
            : kind === "report"
              ? box && { report: [box[0], box[1], reportOre(), reportAmount()] }
              : { units: [design, n] };
  const giveLot = () => (giveKind() === "feed" ? { feed: giveFeed() } : lotOf(giveKind(), give(), giveN(), giveBox(), giveDesign()));
  const takeLot = () => (takeKind() === "feed" ? { feed: takeFeed() } : lotOf(takeKind(), take(), takeN(), null, takeDesign()));
  // A feed's terms, as fields: how late, how long, whose ground.
  const feedFields = (value: () => { delay: number; hours: number; own_ground: boolean }, set: (f: { delay: number; hours: number; own_ground: boolean }) => void) => (
    <span class="lot-fields feed-fields">
      <input type="number" min="1" max="24" value={value().hours} onChange={(e) => set({ ...value(), hours: Math.min(24, Math.max(1, Math.floor(Number(e.currentTarget.value) || 1))) })} aria-label="Hours" />
      <span class="muted small">h,</span>
      <select value={String(value().delay)} onChange={(e) => set({ ...value(), delay: Number(e.currentTarget.value) })} aria-label="How late">
        <For each={[0, 5, 15, 60]}>
          {(d) => (
            <option value={String(d)} selected={d === value().delay}>
              {d ? `${d} min late` : "at once"}
            </option>
          )}
        </For>
      </select>
      <label class="standing">
        <input type="checkbox" checked={value().own_ground} onChange={(e) => set({ ...value(), own_ground: e.currentTarget.checked })} />
        own ground only
      </label>
    </span>
  );
  // Units: which design, among what can be traded.
  const designSelect = (value: () => number, set: (d: number) => void) => (
    <select value={value()} onChange={(e) => set(Number(e.currentTarget.value))}>
      <For each={designsForTrade()}>{(d) => <option value={d.id}>{d.name}</option>}</For>
    </select>
  );
  const unitWorth = (id: number) => ((state()?.designs as { id: number; worth?: number }[] | undefined) ?? []).find((d) => d.id === id)?.worth ?? 0;
  // A lot's reference worth here (findings have none yet).
  const worthOf = (l: Lot | null) =>
    !l ? 0 : Array.isArray(l) ? (prices()[l[0]] ?? 0) * l[1] : "credits" in l ? l.credits : "units" in l ? unitWorth(l.units[0]) * l.units[1] : 0;
  const lotGood = (l: Lot) => (Array.isArray(l) ? l[0] : null);
  // `by`: who gives the lot (a report is their claim, in their name).
  const lotText = (l: Lot, by?: number) =>
    Array.isArray(l)
      ? amount(l[1], l[0])
      : "credits" in l
        ? `${Math.round(l.credits).toLocaleString("en-US")} credits`
        : "findings" in l
          ? `prospecting in ${l.findings[0].join(", ")} – ${l.findings[1].join(", ")}`
          : "sightings" in l
            ? `last seen in ${l.sightings[0].join(", ")} – ${l.sightings[1].join(", ")}`
            : "feed" in l
              ? `${l.feed.hours} h of what ${by === me() ? "you" : by === undefined ? "they" : sideName(by)} see${by === me() ? "" : by === undefined ? "" : "s"}, ${l.feed.delay ? `${l.feed.delay} min late` : "at once"}${l.feed.own_ground ? ", own ground only" : ""}`
              : "report" in l
              ? `Claim: ${l.report[3].toLocaleString("en-US")} ${l.report[2]} per cell${by === undefined ? "" : `, by ${by === me() ? "you" : sideName(by)}`} (${l.report[0].join(", ")} – ${l.report[1].join(", ")})`
              : `${l.units[1]} × ${designName(l.units[0])}`;
  const designName = (id: number) => designsForTrade().find((d) => d.id === id)?.name ?? ((state()?.designs as { id: number; name: string }[] | undefined) ?? []).find((d) => d.id === id)?.name ?? `design ${id}`;
  const cr = (n: number | undefined) => (n ? `≈ ${Math.round(n).toLocaleString("en-US")} cr` : "");
  // A lot's worth beside it, unless it is credits (it says so already).
  const crOf = (l: Lot, n: number | undefined) => (!Array.isArray(l) && "credits" in l ? "" : cr(n));
  // From the taker's side: what they get (give) over what they pay (take).
  const fairness = (w: [number, number] | undefined) => {
    if (!w || !w[0] || !w[1]) return null;
    const d = (w[0] - w[1]) / w[1];
    return Math.abs(d) < 0.1 ? { text: "about fair", cls: "" } : { text: `${d > 0 ? "+" : "−"}${Math.round(Math.abs(d) * 100)}% for you`, cls: d > 0 ? "up" : "down" };
  };
  const post = () => {
    const g = giveLot();
    const t = takeLot();
    if (!g || !t) return;
    const good0 = lotGood(g);
    const from = good0 !== null ? richest(good0) : stations()[0];
    if (from) send({ type: "offer", at: from.id, give: g, take: t, ...(standing() ? { repeat: true } : {}) });
  };
  const accept = (o: Offer) => {
    const g = lotGood(o.take) ?? lotGood(o.give);
    const at = g !== null ? richest(g) : stations()[0];
    if (at) send({ type: "accept", offer: o.id, at: at.id });
  };
  // A side of the offer: what kind, and its amount.
  // How much a lot is, for a contract's bar (findings go over at once).
  const lotSize = (l: Lot) => (Array.isArray(l) ? l[1] : "credits" in l ? l.credits : "units" in l ? l.units[1] : 1);
  const kindSelect = (value: () => Kind, set: (k: Kind) => void) => (
    <select value={value()} onChange={(e) => set(e.currentTarget.value as Kind)}>
      <option value="goods">Goods</option>
      <option value="credits">Credits</option>
      <option value="findings">Prospecting</option>
      <option value="sightings">Last seen</option>
      <option value="report">Report</option>
      <option value="units">Units</option>
      <option value="feed">A feed of what we see</option>
    </select>
  );
  const goodSelect = (value: () => number, set: (g: number) => void) => (
    <select value={value()} onChange={(e) => set(Number(e.currentTarget.value))}>
      <For each={TRADED}>{(g) => <option value={g}>{goods()[g] ?? g}</option>}</For>
    </select>
  );
  const number = (value: () => number, set: (n: number) => void) => (
    <input type="number" min="1" step="100" value={value()} onInput={(e) => set(Math.max(0, Math.floor(Number(e.currentTarget.value) || 0)))} />
  );
  return (
    <section class="depth-screen market" aria-label="Market">
      <div class="market-offers">
        <h2>Offers</h2>
        <Show when={market().offers.length} fallback={<p class="muted">No one is offering anything.</p>}>
          <table>
            <thead>
              <tr>
                <th>From</th>
                <th>Gives</th>
                <th>Wants</th>
                <th />
              </tr>
            </thead>
            <tbody>
              <For each={market().offers}>
                {(o) => (
                  <tr>
                    <td>
                      {who(o.seller)}
                      <Show when={o.repeat}>
                        <span class="muted small" data-tip="A standing deal: once taken it renews round after round, until either side ends it">
                          {" "}
                          · standing
                        </span>
                      </Show>
                    </td>
                    <td>
                      {lotText(o.give, o.seller)} <span class="muted small">{crOf(o.give, o.worth?.[0])}</span>
                    </td>
                    <td classList={{ short: o.seller !== me() && (Array.isArray(o.take) ? total(o.take[0]) < o.take[1] : "credits" in o.take ? credits() < o.take.credits : false) }}>
                      {lotText(o.take, o.seller === me() ? undefined : me())} <span class="muted small">{crOf(o.take, o.worth?.[1])}</span>
                      <Show when={o.seller !== me() && fairness(o.worth)}>
                        {(f) => (
                          <span class={`fair small ${f().cls}`} data-tip="By reference worth (what each side costs to make), not a price anyone must use">
                            {" "}
                            · {f().text}
                          </span>
                        )}
                      </Show>
                    </td>
                    <td>
                      <Show
                        when={o.seller === me()}
                        fallback={
                          <button class="button" disabled={!stations().length} onClick={() => accept(o)}>
                            Accept
                          </button>
                        }
                      >
                        <button class="button" onClick={() => send({ type: "withdraw", offer: o.id })}>
                          Withdraw
                        </button>
                      </Show>
                    </td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </Show>
      </div>
      <div class="market-side">
        <AiMarket />
        <h2>Make an offer</h2>
        <div class="offer-form">
          <span class="row-label">Give</span>
          {kindSelect(giveKind, setGiveKind)}
          <Show when={giveKind() === "goods"}>
            <span class="lot-fields">
              {number(giveN, setGiveN)}
              {goodSelect(give, setGive)}
            </span>
            <span />
            <span class="muted small lot-note">{total(give()).toLocaleString("en-US")} held</span>
          </Show>
          <Show when={giveKind() === "credits"}>
            <span class="lot-fields">{number(giveN, setGiveN)}</span>
            <span />
            <span class="muted small lot-note">{Math.floor(credits()).toLocaleString("en-US")} credits held</span>
          </Show>
          <Show when={giveKind() === "units"}>
            <span class="lot-fields">
              {number(giveN, setGiveN)}
              {designSelect(giveDesign, setGiveDesign)}
            </span>
            <span />
            <span class="muted small lot-note">
              {(designsForTrade().find((d) => d.id === giveDesign())?.have ?? 0).toLocaleString("en-US")} of yours; idle ones nearest the station go, walking to theirs
            </span>
          </Show>
          <Show when={giveKind() === "findings" || giveKind() === "sightings" || giveKind() === "report"}>
            <span class="lot-fields">
              <button class="button" onClick={() => boxPicker((b) => setGiveBox(b))}>
                {giveBox() ? `${giveBox()![0].join(", ")} – ${giveBox()![1].join(", ")}` : "Pick a box on the map"}
              </button>
            </span>
            <Show when={giveKind() === "report"}>
              <span />
              <span class="lot-fields">
                {number(reportAmount, setReportAmount)}
                <select value={reportOre()} onChange={(e) => setReportOre(e.currentTarget.value)}>
                  <For each={REPORT_ORES}>{(o) => <option value={o}>{oreLabel(oreKey(o)).toLowerCase()} per cell</option>}</For>
                </select>
              </span>
            </Show>
            <span />
            <span class="muted small lot-note">
              {giveKind() === "findings"
                ? "What you've found there, copied to them at once."
                : giveKind() === "sightings"
                  ? "What you last saw there (forces, claims), each as old as it is; copied to them at once."
                  : "Your claim, true or not: they see it on their map as yours, beside anything they've surveyed themselves."}
            </span>
          </Show>
          <Show when={giveKind() === "feed"}>
            {feedFields(giveFeed, setGiveFeed)}
            <span />
            <span class="muted small lot-note">What you see (your last sightings, not live sight), sent to them for these hours, this late.</span>
          </Show>
          <span class="row-label">For</span>
          {kindSelect(takeKind, setTakeKind)}
          <Show when={takeKind() === "feed"}>
            {feedFields(takeFeed, setTakeFeed)}
          </Show>
          <Show when={takeKind() === "goods"}>
            <span class="lot-fields">
              {number(takeN, setTakeN)}
              {goodSelect(take, setTake)}
            </span>
          </Show>
          <Show when={takeKind() === "credits"}>
            <span class="lot-fields">{number(takeN, setTakeN)}</span>
          </Show>
          <Show when={takeKind() === "units"}>
            <span class="lot-fields">
              {number(takeN, setTakeN)}
              {designSelect(takeDesign, setTakeDesign)}
            </span>
          </Show>
          <Show when={takeKind() === "findings" || takeKind() === "sightings" || takeKind() === "report"}>
            <span class="lot-fields muted small">Theirs: offer it the other way round.</span>
          </Show>
          <span />
          <span class="muted small lot-note">
            Worth {cr(worthOf(giveLot())) || "unknown"} for {cr(worthOf(takeLot())) || "unknown"}
          </span>
          <span />
          <label class="standing" data-tip="Renews round after round once taken, until either side ends it">
            <input type="checkbox" checked={standing()} onChange={(e) => setStanding(e.currentTarget.checked)} />
            Standing deal
          </label>
          <span />
          <button
            class="button primary"
            disabled={!stations().length || !giveLot() || !takeLot() || JSON.stringify(giveLot()) === JSON.stringify(takeLot()) || (giveKind() === "credits" && giveN() > credits())}
            onClick={post}
          >
            Post offer
          </button>
        </div>
        <h2>Your contracts</h2>
        <Show when={market().contracts.length} fallback={<p class="muted">None yet.</p>}>
          <For each={market().contracts}>
            {(k) => (
              <div class="contract">
                <span class="contract-head">
                  {who(k.seller)} → {who(k.buyer)}
                  <Show when={k.moving?.length}>
                    <span class="muted small" data-tip="Units walking to their new owner's station; they change hands within 8 hexes of it">
                      {" "}
                      · {k.moving!.length} on the way
                    </span>
                  </Show>
                  <Show when={k.repeat}>
                    <span class="muted small">
                      {" "}
                      · standing · {(k.rounds ?? 0).toLocaleString("en-US")} round{k.rounds === 1 ? "" : "s"}
                    </span>
                    <button class="ghost end-deal" data-tip="Finishes the round under way, then closes" onClick={() => send({ type: "withdraw", offer: k.id })}>
                      End deal
                    </button>
                  </Show>
                </span>
                <span class="contract-line">
                  <span>
                    {lotText(k.give, k.seller)} <span class="muted small">{crOf(k.give, k.worth?.[0])}</span>
                  </span>
                  <span class="bar">
                    <span style={{ width: `${Math.min(100, (100 * k.sent) / Math.max(1, lotSize(k.give)))}%` }} />
                  </span>
                </span>
                <span class="contract-line">
                  <span>
                    {lotText(k.take, k.buyer)} <span class="muted small">{crOf(k.take, k.worth?.[1])}</span>
                  </span>
                  <span class="bar">
                    <span style={{ width: `${Math.min(100, (100 * k.paid) / Math.max(1, lotSize(k.take)))}%` }} />
                  </span>
                </span>
              </div>
            )}
          </For>
        </Show>
      </div>
    </section>
  );
}

function Screens(): JSX.Element {
  return (
    <>
      <Show when={screen() === "research"}>
        <ResearchScreen />
      </Show>
      <Show when={screen() === "market"}>
        <MarketScreen />
      </Show>
      <Show when={screen() === "production"}>
        <ProductionScreen />
      </Show>
      <Show when={screen() === "admin"}>
        <AdminPanel sides={factions} />
      </Show>
      <Show when={screen() === "diplomacy"}>
        <DiplomacyScreen sides={factions} send={(c) => send(c)} />
      </Show>
      <Show when={screen() === "designs"}>
        <DesignScreen state={state} send={(c) => send(c)} bake={(s) => bake(s)} toast={(t) => toast(t)} />
      </Show>
      <GuideScreen />
    </>
  );
}

/** Mounts the screens into `el`, sending commands with `sender` and asking
 *  for design previews with `baker`; `sides` names and colours players. */
export function mountScreens(
  el: HTMLElement,
  sender: (command: Record<string, unknown>) => void,
  baker: (spec: object) => Promise<Record<string, unknown>>,
  sides: { name: string; color: string }[],
  toaster: (text: string) => void = () => {},
  unitDesigns: () => { id: number; name: string; have: number }[] = () => [],
): () => void {
  toast = toaster;
  designsForTrade = unitDesigns;
  send = sender;
  bake = baker;
  factions = sides;
  return render(() => <Screens />, el);
}
